import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import digitalDetoxHero from '@/assets/digital-detox-hero.jpg';

const DigitalDetox = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Mindfulness</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Digital Detox – Why Our Brain Needs Silence
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>June 1, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>8 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/83fe081b-c262-4d29-b438-860b88fdcd93.png" 
              alt="Digital detox concept - restoring brain's natural ability to focus"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Imagine a morning without your phone. You wake up, and instead of hearing a notification ping, you open the window and let fresh air into the room. You hear birdsong, smell fresh coffee, and your breath slowly deepens. There are no alerts demanding your attention. Just you, and a moment of silence.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <p>
                For many of us, this image feels distant. The phone is the first and last thing we touch each day. We live in a world where silence has been replaced by a constant stream of information. And that's precisely why more and more people are discovering the value of a <strong className="text-accent">digital detox</strong> – a conscious disconnection from technology.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Living in a World of Notifications</h2>

              <p>
                Smartphones have become our constant companions, for better or worse. Research from Deloitte shows that, on average, we check our phones <strong className="text-accent">58 times a day</strong>, and more than half of those interactions are "habitual," without any specific purpose.
              </p>

              <p>
                The average time spent in front of a smartphone screen now exceeds <strong className="text-accent">5 hours per day</strong>. That means a significant part of our lives unfolds in the digital world – at the cost of connection with ourselves, with others, and with nature.
              </p>

              <p>
                Every notification works like a reward stimulus: a quick dopamine hit that feels good – but only for a moment. At the same time, it creates micro-stress that keeps our brain in a state of alertness. Studies published in the <em>Journal of Experimental Psychology</em> show that even <strong className="text-accent">the mere sound of a notification reduces our ability to concentrate</strong>, even if we don't pick up the phone.
              </p>

              <p>
                The point isn't to demonize technology. It can make life easier, support learning, and help us stay connected with loved ones. The problem begins when the tool starts to control us instead of serving us.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Silence as Medicine</h2>

              <p>
                Silence is increasingly rare. Yet research shows it has a real impact on both our mental and physical health. An experiment at Duke University found that <strong className="text-accent">just two hours per week spent in nature significantly improves well-being and reduces stress</strong>.
              </p>

              <p>
                Not coincidentally, Japan has developed the practice of <em>shinrin-yoku</em>, or "forest bathing." It means immersing yourself in the atmosphere of the forest – touching the bark, listening to the rustle of leaves, noticing the light filtering through the trees. Simple? Yes. And yet, studies published in <em>Frontiers in Psychology</em> show that regular walks in the forest lower cortisol (the stress hormone), improve mood, and support immunity.
              </p>

              <p>
                Silence and contact with nature bring us back to balance. They are the moments when we can truly hear ourselves.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Digital Detox in Practice</h2>

              <p>
                Does this mean we have to abandon technology altogether? Absolutely not. A digital detox is not an escape from the world but a <strong className="text-accent">pause that allows us to set healthier boundaries</strong>.
              </p>

              <p>A few simple steps you can try today:</p>
              
              <ul className="space-y-3 ml-6">
                <li><strong className="text-accent">One offline hour daily</strong> – put your phone aside and spend the time walking, reading, or talking.</li>
                <li><strong className="text-accent">A weekend without notifications</strong> – turn them off completely and see how your mood shifts.</li>
                <li><strong className="text-accent">Screen-free zones</strong> – for example, the bedroom or dining table. Spaces reserved for rest and connection.</li>
                <li><strong className="text-accent">Breath instead of scroll</strong> – when you reach for your phone out of habit, pause and take three deep breaths.</li>
              </ul>

              <p>
                These small changes add up. Over time, you may notice better sleep, deeper conversations, and a calmer everyday rhythm. A study in <em>Computers in Human Behavior</em> found that people who practiced short breaks from smartphones reported <strong className="text-accent">greater happiness and less anxiety</strong>.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Returning to Ourselves</h2>

              <p>
                A digital detox is not a trend or a luxury. It's a response to our body's and mind's natural needs. It's a return to simplicity, to presence, to the here and now. A reminder that the world doesn't just exist inside the screen – but within us and around us.
              </p>

              <p>
                So perhaps it's worth asking: <em>Do I really need to be online right now?</em> And maybe the better answer is: <em>No, right now I need silence.</em>
              </p>

              <p className="text-xl font-light italic text-accent">
                Because real change often begins with a simple gesture – a conscious breath in the quiet.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Deloitte Global Mobile Consumer Survey 2023.</p>
                  <p>2. DataReportal 2024: Digital Global Overview Report.</p>
                  <p>3. Stothart, C., Mitchum, A., & Yehnert, C. (2015). <em>The attentional cost of receiving a cell phone notification</em>. Journal of Experimental Psychology.</p>
                  <p>4. White, M. P., et al. (2019). <em>Spending at least 120 minutes a week in nature is associated with good health and wellbeing</em>. Scientific Reports, 9(1).</p>
                  <p>5. Hansen, M. M., Jones, R., & Tocchini, K. (2017). <em>Shinrin-yoku (forest bathing) and nature therapy: A state-of-the-art review</em>. International Journal of Environmental Research and Public Health.</p>
                  <p>6. Przybylski, A. K., & Weinstein, N. (2017). <em>Digital detox: The effect of smartphone abstinence on mood, anxiety, and craving</em>. Computers in Human Behavior, 68, 221–228.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default DigitalDetox;